import React, { Component } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { browser } from "@formant/core/common/browser";
import { logger } from "@formant/core/common/logger";
import { IDeviceTeleopConfiguration } from "@formant/core/model/IDeviceTeleopConfiguration";
import { rtcAudioChunkStreamType } from "@formant/core/model/rtcStreamTypes";
import { IRtcStreamMessage } from "@formant/rtc-client/models/IRtcStreamMessage";

import { operateStore } from "../../stores";
import { CircleButton } from "../common/CircleButton";
import { WindowEvent } from "../common/WindowEvent";
import * as styles from "./TeleopAudioPlayer.scss";
import { stringToArrayBuffer } from "./video/h264";

export interface ITeleopAudioPlayerProps {
    configuration: IDeviceTeleopConfiguration;
}

@observer
export class TeleopAudioPlayer extends Component<ITeleopAudioPlayerProps> {
    @observable
    private muted?: boolean;
    @observable
    private hasReceivedData: boolean = false;
    private audioContext?: AudioContext;
    private source?: AudioBufferSourceNode;
    private unregister?: () => void;
    private chunks: Array<AudioBufferSourceNode> = [];
    private isPlaying: boolean = false;
    private startTime: number = 0;
    private lastChunkOffset: number = 0;
    private bufferSize: number = 3;
    private streamName?: string;

    public componentDidMount() {
        const { session } = operateStore;
        const { configuration } = this.props;

        const audioRosStreamName = configuration?.rosStreams?.find(
            ({ topicType }) => topicType === "audio_common_msgs/AudioData",
        )?.topicName;
        const audioHardwareStreamName = configuration?.hardwareStreams?.find(
            stream => stream.rtcStreamType === rtcAudioChunkStreamType,
        )?.name;

        this.streamName = audioRosStreamName || audioHardwareStreamName;

        if (!this.streamName) {
            return;
        }

        if (browser() === "Safari" || browser() === "IE") {
            this.changeAudioWireFormat("wav");
        } else {
            this.changeAudioWireFormat("opus");
        }

        this.unregister = session?.registerReceiver(
            this.streamName,
            this.receive,
        );

        const AudioContext =
            window.AudioContext || (window as any).webkitAudioContext;

        this.audioContext = new AudioContext();
    }

    public componentWillUnmount() {
        this.unregister?.();
    }

    public render() {
        const { muted, hasReceivedData, streamName } = this;

        if (!streamName) {
            return false;
        }

        const unmuteEventTypes: Array<keyof WindowEventMap> = [
            "auxclick",
            "click",
            "contextmenu",
            "dblclick",
            "keydown",
            "keyup",
            "mousedown",
            "mouseup",
            "touchend",
        ];

        const unmuteListeners = unmuteEventTypes.map(_ => (
            <WindowEvent key={_} type={_} onEvent={this.onDoAnything} />
        ));

        return (
            <div
                className={classnames(
                    styles["teleop-audio-player"],
                    muted && styles.muted,
                    hasReceivedData && styles.hasReceivedData,
                )}
            >
                {muted === undefined && unmuteListeners}
                {muted !== false ? (
                    <CircleButton
                        className={styles.icon}
                        icon="audio-muted"
                        onClick={this.onPlay}
                    />
                ) : (
                    <CircleButton
                        className={styles.icon}
                        icon="audio"
                        onClick={this.onPause}
                    />
                )}
            </div>
        );
    }

    private onDoAnything = async () => {
        if (this.muted === undefined) {
            await this.onPlay();
        }
    };

    private onPlay = async () => {
        if (this.audioContext?.state === "suspended") {
            await this.audioContext?.resume();
        }

        this.muted = false;
    };

    private onPause = async () => {
        await this.audioContext?.suspend();
        this.source?.stop(0);
        this.muted = true;
    };

    public receive = async (msg: IRtcStreamMessage) => {
        const data = msg.payload.audioChunk?.chunk_data;
        if (!data) {
            return;
        }

        if (!this.hasReceivedData) {
            this.hasReceivedData = true;
        }

        const { audioContext, muted } = this;
        if (
            !audioContext ||
            msg.header.stream.streamType !== rtcAudioChunkStreamType ||
            muted !== false
        ) {
            return;
        }
        const arrayBuffer = stringToArrayBuffer(data);
        try {
            // stringToArrayBuffer uses Uint8Array.from() which always creates a new ArrayBuffer
            // (never SharedArrayBuffer), so this type assertion is safe
            await audioContext.decodeAudioData(
                arrayBuffer.buffer as ArrayBuffer,
                this.scheduleChunk,
            );
        } catch (error) {
            logger.warn(
                "Error decoding audio buffer, changing audioWireFormat on agent",
                { error },
            );
            this.changeAudioWireFormat("wav");
        }
    };

    private scheduleChunk = (buffer: AudioBuffer) => {
        const { audioContext } = this;

        if (!audioContext) {
            return;
        }
        if (this.chunks.length > this.bufferSize || this.isPlaying === false) {
            this.chunks.forEach(c => {
                c.stop();
            });
            this.isPlaying = false;
            this.chunks = [];
        }
        const chunk = this.createChunk(buffer);
        if (!chunk) {
            return;
        }
        if (!chunk.buffer) {
            return;
        }
        if (this.isPlaying === false) {
            this.startTime = audioContext.currentTime;
            this.lastChunkOffset = 0;
            this.isPlaying = true;
        }
        chunk.start(this.startTime + this.lastChunkOffset, 0, buffer.duration);
        this.lastChunkOffset += chunk.buffer.duration;
        this.chunks.push(chunk);
    };

    private createChunk(buffer: AudioBuffer) {
        const { audioContext } = this;
        if (!audioContext) {
            return;
        }
        const source = audioContext.createBufferSource();
        source.buffer = buffer;
        source.connect(audioContext.destination);
        source.loop = false;
        source.onended = (_: Event) => {
            this.chunks.splice(this.chunks.indexOf(source), 1);
            if (this.chunks.length === 0) {
                this.isPlaying = false;
            }
        };

        return source;
    }

    private changeAudioWireFormat(newFormat: string) {
        const { streamName } = this;
        const session = operateStore?.session;
        if (!session || !streamName) {
            return;
        }
        session.controlRemoteStream({
            streamName,
            setAudioFormat: newFormat,
        });
    }
}
